// Copyright (c) 2026 PGNS LLC
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { createContext, useContext, useLayoutEffect, useState } from 'react';

export type Theme = 'dark' | 'light' | 'system';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Not every environment that runs this component has a Storage implementation:
// SSR has no window, and happy-dom under Node leaves `localStorage` undefined.
// Theme persistence is best-effort -- never let a missing Storage break render.
function themeStorage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : (window.localStorage ?? undefined);
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  forcedTheme?: Exclude<Theme, 'system'>;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'pgns-theme',
  forcedTheme,
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (themeStorage()?.getItem(storageKey) as Theme | null) ?? defaultTheme,
  );

  const effectiveTheme = forcedTheme ?? theme;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');

    if (effectiveTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(effectiveTheme);
    }

    if (!forcedTheme) themeStorage()?.setItem(storageKey, theme);
  }, [effectiveTheme, forcedTheme, storageKey, theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme: effectiveTheme,
        setTheme: (nextTheme: Theme) => {
          if (!forcedTheme) setTheme(nextTheme);
        },
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
